import SDKConfigurationModel from "../../Configurator/Models/SDKConfiguraitonModel";
import MiniGamesFacade from "./Facade/MiniGamesFacade";
import { IEitherOrParticipationCallback } from "./Interfaces/MiniGamesInterfaces";
import ClassicQuizFilters from "./Models/ClassicQuiz/ClassicQuizFilters";
import ClassicQuizLeaderboardFilters from "./Models/ClassicQuiz/Rank/ClassicQuizLeaderboardFilters";
import ClassicQuizParticipationBodyModel from "./Models/ClassicQuiz/ClassicQuizParticipationBodyModel";
import ClassicQuizParticipationsFilters from "./Models/ClassicQuiz/ClassicQuizParticipationsFilters";
import ClassicQuizRankFilters from "./Models/ClassicQuiz/Rank/ClassicQuizRankFilters";
import EitherOrFilters from "./Models/EitherOr/EitherOrFilters";
import EitherOrParticipationRequestModel from "./Models/EitherOr/Participation/EitherOrParticipationRequestModel";
import PersonalityQuizFilters from "./Models/PersonalityQuiz/PersonalityQuizFilters";
import PersonalityQuizParticipationBodyModel from "./Models/PersonalityQuiz/Participation/PersonalityQuizParticipationBodyModel";
import PersonalityQuizParticipationsFilters from "./Models/PersonalityQuiz/Participation/PersonalityQuizParticipationsFilters";

export default class MiniGames {
  private facade: MiniGamesFacade = null;

  constructor(config: SDKConfigurationModel) {
    this.facade = new MiniGamesFacade(config);
  }

  public getClassicQuizzes = async (
    filters?: ClassicQuizFilters,
    showFullContextData?: boolean,
    disableCache?: boolean
  ) => {
    return await this.facade.getClassicQuizzes(
      filters,
      showFullContextData,
      disableCache
    );
  };

  public getClassicQuizById = async (
    classicQuizId: string,
    includeCorrectOptionIds?: boolean,
    disableCache?: boolean
  ) => {
    return await this.facade.getClassicQuizById(
      classicQuizId,
      includeCorrectOptionIds,
      disableCache
    );
  };

  public participateInClassicQuiz = async (
    classicQuizId: string,
    questions: ClassicQuizParticipationBodyModel[],
    includeCorrectOptionIds?: boolean
  ) => {
    return await this.facade.participateInClassicQuiz(
      classicQuizId,
      questions,
      includeCorrectOptionIds
    );
  };

  public getMyClassicQuizParticipations = async (
    filters?: ClassicQuizParticipationsFilters
  ) => {
    return await this.facade.getMyClassicQuizParticipations(filters);
  };

  public getMyClassicQuizParticipationsWithPagination = async (
    filters?: ClassicQuizParticipationsFilters
  ) => {
    return await this.facade.getMyClassicQuizParticipationsWithPagination(
      filters
    );
  };

  public getLeaderboardForClassicQuizzes = async (
    filters?: ClassicQuizLeaderboardFilters,
    disableCache?: boolean
  ) => {
    return await this.facade.getLeaderboardForClassicQuizzes(
      filters,
      disableCache
    );
  };

  public getClassicQuizzesUserRanking = async (
    userId: string,
    filters?: ClassicQuizRankFilters,
    disableCache?: boolean
  ) => {
    return await this.facade.getClassicQuizzesUserRanking(
      userId,
      filters,
      disableCache
    );
  };

  public getClassicQuizQuestionsReport = async (
    classicQuizId: string,
    includeCorrectOptionIds?: boolean
  ) => {
    return await this.facade.getClassicQuizQuestionsReport(
      classicQuizId,
      includeCorrectOptionIds
    );
  };

  public getClassicQuizResultsSummary = async (
    classicQuizId: string,
    disableCache?: boolean
  ) => {
    return await this.facade.getClassicQuizResultsSummary(
      classicQuizId,
      disableCache
    );
  };

  public getEitherOrs = async (
    filters?: EitherOrFilters,
    showFullContextData?: boolean,
    disableCache?: boolean
  ) => {
    return await this.facade.getEitherOrs(
      filters,
      showFullContextData,
      disableCache
    );
  };

  public getEitherOrById = async (
    eitherOrId: string,
    disableCache?: boolean
  ) => {
    return await this.facade.getEitherOrById(eitherOrId, disableCache);
  };

  public getMyEitherOrStats = async (eitherOrId: string) => {
    return await this.facade.getMyEitherOrStats(eitherOrId);
  };

  public getEitherOrResults = async (
    eitherOrId: string,
    disableCache?: boolean
  ) => {
    return await this.facade.getEitherOrResults(eitherOrId, disableCache);
  };

  public participateInEitherOr = async (
    eitherOrId: string,
    participation: EitherOrParticipationRequestModel | null,
    onEitherOrParticipationCallbacks?: IEitherOrParticipationCallback
  ) => {
    return await this.facade.participateInEitherOr(
      eitherOrId,
      participation,
      onEitherOrParticipationCallbacks
    );
  };

  public calculateEitherOrResultPercentile = async (
    eitherOrId: string,
    points: number
  ) => {
    return await this.facade.calculateEitherOrResultPercentile(
      eitherOrId,
      points
    );
  };

  public getPersonalityQuizzes = async (
    filters?: PersonalityQuizFilters,
    showFullContextData?: boolean,
    disableCache?: boolean
  ) => {
    return await this.facade.getPersonalityQuizzes(
      filters,
      showFullContextData,
      disableCache
    );
  };

  public getPersonalityQuizById = async (
    personalityQuizId: string,
    disableCache?: boolean
  ) => {
    return await this.facade.getPersonalityQuizById(
      personalityQuizId,
      disableCache
    );
  };

  public participateInPersonalityQuiz = async (
    personalityQuizId: string,
    questions: PersonalityQuizParticipationBodyModel[]
  ) => {
    return await this.facade.participateInPersonalityQuiz(
      personalityQuizId,
      questions
    );
  };

  public getPersonalityQuizUserParticipation = async (
    personalityQuizId: string,
    profileId: string,
    disableCache?: boolean
  ) => {
    return await this.facade.getPersonalityQuizUserParticipation(
      personalityQuizId,
      profileId,
      disableCache
    );
  };

  public getMyPersonalityQuizParticipations = async (
    filters?: PersonalityQuizParticipationsFilters
  ) => {
    return await this.facade.getMyPersonalityQuizParticipations(filters);
  };
}
